<template>
    <div class="overview-container">
        <alert :show-alert="showAlert" :message="alertMessage" :type="alertType" />
        <div v-if="getCurrentSMSCampaign" class="info-container">
            <v-card class="table-wrapper">
                <campaign-table :rows="campaignTableRows" />
                <delete-dialog
                    v-if="hasDeletePrivilege"
                    :disable-delete="hasDeletePrivilege"
                    class="delete-dialog"
                    @delete-event="deleteSMSCampaign()"
                />
            </v-card>
            <v-card
                v-if="statusCounts.length > 0"
                class="charts-container"
            >
                <radial-bar :progress="progress" />
                <donut-chart :status-counts="statusCounts" />
                <simple-table :status-counts="statusCounts" />
            </v-card>
        </div>
        <v-card
            class="table-container"
        >
            <div class="card-title">
                <div>{{ cardTitle }}</div>
            </div>
            <array-view
                :items="smsMessages"
                :headers="headers"
                :total-pages="totalPages"
                :page="page"
                :size="size"
                :total-items="totalItems"
                :has-search-field="true"
                :update-filter="updateFilter"
                :is-fetching="isFetchingSms"
                should-rows-expand
                @paginationChange="paginationChange"
            />
        </v-card>
    </div>
</template>

<script>
    import dateTimeUtils from '@/utils/date-time-utils'
    import recurringSmsCampaignService from '@/services/recurring-sms-campaign-service'
    import RadialBar from '@/components/RadialBar.vue'
    import DonutChart from '@/components/DonutChart.vue'
    import SimpleTable from '@/components/SimpleTable.vue'
    import ArrayView from '@/components/ArrayView.vue'
    import CampaignTable from '@/components/CampaignTable.vue'
    import tableUtils from '@/utils/tableUtils'
    import { hasPrivilege } from '@/utils/userUtils'
    import { PRIVILEGES } from '@/utils/consts'
    import DeleteDialog from '@/components/DeleteDialog.vue'
    import Alert from '@/components/Alert.vue'
    export default {
        components: {
            CampaignTable,
            RadialBar,
            DonutChart,
            SimpleTable,
            ArrayView,
            DeleteDialog,
            Alert
        },
        props: {
            campaign: {
                type: Object,
                required: false,
                default: null
            }
        },
        data() {
            return {
                showAlert: false,
                alertMessage: '',
                alertType: 'error',
                submitLoading: false,
                statusCounts: [],
                progress: 0,
                headers: [
                    {
                        text: '#',
                        value: 'index'
                    },
                    {
                        text: 'Id',
                        value: 'id'
                    },
                    { text: 'Mobile Number', value: 'recipient' },
                    { text: 'Text', value: 'smsText' },
                    { text: 'SMS Parts', value: 'parts' },
                    { text: 'Date Sent', value: 'sentDate' },
                    { text: 'Status', value: 'status' },
                    { text: 'Date of Status', value: 'statusDate' },
                    { text: '', value: 'data-table-expand' }
                ],
                totalPages: 0,
                size: 5,
                page: 1,
                smsMessages: [],
                cardTitle: 'SMS List',
                csvString: '',
                totalItems: 0,
                smsFilter: '',
                isFetchingSms: false
            }
        },
        computed: {
            hasDeletePrivilege() {
                return hasPrivilege(
                    PRIVILEGES.RECURRING_SMS_CAMPAIGN_DELETE_PRIVILEGE,
                    this.$store.getters.getCurrentUserPrivileges
                )
            },
            campaignTableRows() {
                return [
                    {
                        header: 'Id :',
                        value: this.getCurrentSMSCampaign.id
                    },
                    {
                        header: 'Campaign :',
                        value: this.getCurrentSMSCampaign.alias
                    },
                    {
                        header: 'Originator :',
                        value: this.getCurrentSMSCampaign.originator
                    },
                    {
                        header: 'SMS Template :',
                        value: this.getCurrentSMSCampaign.textTemplate
                    },
                    {
                        header: 'Alias :',
                        value: this.getCurrentSMSCampaign.alias
                    },
                    {
                        header: 'Created At :',
                        value: this.getHumanizedDateTime(this.getCurrentSMSCampaign.createdAt)
                    }
                ]
            },
            getCurrentSMSCampaign() {
                return this.campaign
            },
            filter() {
                return {
                    filter: { q: this.smsFilter },
                    range: [this.page - 1, this.size]
                }
            }
        },
        watch: {
            getCurrentSMSCampaign() {
                this.initDetails()
            },
            showAlert: {
                handler(newval) {
                    if (newval) {
                        setTimeout(() => { this.showAlert = false }, 3000)
                    }
                }
            }
        },
        created() {
            this.initDetails()
        },
        methods: {
            initDetails() {
                this.fetchAllSMSMessages()
            },
            getHumanizedDateTime(dateTime) {
                return dateTimeUtils.getHumanizedDateTime(dateTime)
            },
            paginationChange(page, size) {
                this.size = size
                this.page = page
                this.fetchAllSMSMessages()
            },
            fetchAllSMSMessages() {
                this.isFetchingSms = true
                return recurringSmsCampaignService
                    .getSMS(this.getCurrentSMSCampaign.id, this.filter)
                    .then((resp) => {
                        this.smsMessages = resp.data.content.map((message, index) => ({
                            ...message,
                            index: index + (this.page - 1) * this.size + 1,
                            details: this.getMessageDetails(message)
                        }))
                        this.totalPages = resp.data.totalPages
                        this.totalItems = resp.data.totalElements
                    })
                    .finally(() => {
                        this.isFetchingSms = false
                    })
            },
            updateFilter(newFilter) {
                this.smsFilter = newFilter
                this.fetchAllSMSMessages()
            },
            getMessageDetails(message) {
                return tableUtils.getMessageDetails(message)
            },
            deleteSMSCampaign() {
                recurringSmsCampaignService.deleteSMSCampaign(this.getCurrentSMSCampaign.id).then(() => {
                    this.setAlert('Recurring SMS Campaign Successfully Deleted!', 'success')
                    setTimeout(() => {
                        this.$router.push('/recurring-sms')
                    }, 1000)
                }).catch(error => {
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            setAlert(message, type) {
                this.showAlert = true
                this.alertMessage = message
                this.alertType = type
            }
        }
    }
</script>

<style scoped>
.overview-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.info-container {
    display: flex;
    column-gap: 10px;
}

.table-wrapper {
    padding-bottom: 1rem;
    width: 100%;
}

.table-container {
    width: 100%;
}

.card-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
}

.charts-container {
    display: flex;
    flex-direction: row;
    width: 100%;
}

.delete-dialog {
    display: flex;
    justify-content: flex-end;
    padding: 0.5rem;
}

@media screen and (max-width: 992px) {
    .info-container {
        row-gap: 10px;
        flex-wrap: wrap;
    }

    .charts-container {
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }
}

@media (min-width: 993px) {
    .charts-container {
        padding: 2rem 1rem;
    }
}
</style>
